<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';

const props = defineProps({
  align: {
    type: String,
    default: 'right', // left | right | top
  },
  width: {
    type: String,
    default: '48',
  },
  contentClasses: {
    type: Array,
    default: () => ['p-0', 'bg-[#f8f9fa]'],
  },
});

const open = ref(false);

const closeOnEscape = (e) => {
  if (open.value && e.key === 'Escape') open.value = false;
};

onMounted(() => document.addEventListener('keydown', closeOnEscape));
onUnmounted(() => document.removeEventListener('keydown', closeOnEscape));

const widthClass = computed(() => ({
  '44': 'w-44',
  '48': 'w-48',
  '56': 'w-56',
  '64': 'w-64',
  '80': 'w-80',
}[props.width.toString()]));

const alignmentClasses = computed(() => {
  switch (props.align) {
    case 'left':
      return 'ltr:origin-top-left rtl:origin-top-right start-0';
    case 'right':
      return 'ltr:origin-top-right rtl:origin-top-left end-0';
    case 'top':
      return 'left-1/2 -translate-x-1/2 origin-bottom';
    default:
      return 'origin-top';
  }
});
</script>

<template>
  <div class="relative">
    <div @click="open = !open">
      <slot name="trigger" :open="open" />
    </div>

    <!-- Overlay -->
    <div v-show="open" class="fixed inset-0 z-40" @click="open = false" />

    <transition
      enter-active-class="transition ease-out duration-200"
      enter-from-class="transform opacity-0 scale-95"
      enter-to-class="transform opacity-100 scale-100"
      leave-active-class="transition ease-in duration-75"
      leave-from-class="transform opacity-100 scale-100"
      leave-to-class="transform opacity-0 scale-95"
    >
      <div
        v-show="open"
        class="absolute z-50 shadow-lg"
        :class="[
          widthClass,
          alignmentClasses,
          props.align === 'top' ? 'bottom-full mb-2' : 'mt-2'
        ]"
        @click="open = false"
      >
        <div
          class="ring-1 ring-black ring-opacity-5 overflow-hidden"
          :class="contentClasses"
        >
          <slot name="content" />
        </div>
      </div>
    </transition>
  </div>
</template>
